// The report's LinkedIn page: what the searched space posts about, from the LinkedIn posts themselves (server/space.ts):
// topics with their engagement, which formats draw the most reactions, the top posts and the profiles behind them, by
// authors in the research's market. Read from the report; nothing is fetched.
import { useState } from 'react';
import { compact, countryName, fmtDate, nf, plural } from '../lib.jsx';
import { Card, Kpi } from '../charts.jsx';

export const FORMAT_LABEL = {
  text: 'Text only', image: 'One image', images: 'Several images', document: 'Document (PDF carousel)', video: 'Video', article: 'LinkedIn article',
  link: 'Link to a page', poll: 'Poll', newsletter: 'Newsletter', repost: 'Repost with comment',
};
const FEW = 3;

export const hasSpace = (r) => Object.keys(r?.space ?? {}).length > 0;
/** A country, marked when AI judged it from the words rather than the author's profile. */
export const placeOf = (x) => (x.country ? `${countryName(x.country)}${x.guess ? ' (AI guess)' : ''}` : null);

export function LinkedInPage({ r }) {
  const targets = r.scope.targets.filter((x) => r.space?.[x.id]);
  const [tid, setTid] = useState(null);
  const t = targets.find((x) => x.id === tid) ?? targets[0];
  const s = r.space[t.id], market = r.scope.market;
  const where = market === 'GLOBAL' ? '' : ` by authors in ${countryName(market)}`;
  return (
    <div className="ads-page space-page">
      <div className="ads-head">
        <div>
          <h2 className="seg-title">LinkedIn: what the space posts</h2>
          <p className="muted small">What LinkedIn posts about {targets.map((x) => x.name).join(' and ')}{where} talk about, which formats draw the most reactions, and who posts them. From the posts themselves, not their comments.</p>
        </div>
      </div>
      {targets.length > 1 ? (
        <div className="seg ads-targets" role="group" aria-label="Showing">
          {targets.map((x) => <button key={x.id} type="button" aria-pressed={x.id === t.id} onClick={() => setTid(x.id)}>{x.name}</button>)}
        </div>
      ) : null}
      <div className="dgrid">
        <SpaceKpis s={s} name={t.name} />
        <Topics s={s} />
        <Formats s={s} />
        <TopPosts s={s} />
        <Profiles s={s} />
        {s.worldwide?.length ? <TopPosts s={s} worldwide /> : null}
        <Card span={12} title="How to read these numbers" denom={null}>
          <p className="small muted">These are the LinkedIn posts the research found about {t.name}{where} within its dates, by LinkedIn’s own relevance order, not every post on LinkedIn. Engagement is reactions, comments and reposts at collection. {market === 'GLOBAL' ? '' : `LinkedIn search has no country setting, so the research read more posts and kept those whose author’s profile (or a company’s head office) is in ${countryName(market)}; when a profile has no country, AI judges it from the post and the headline (shown as an AI guess), and posts with no country at all are kept too, counted apart. Leading posts worldwide are by authors elsewhere: shown for reference, never in the counts. `}Topics are AI’s grouping of the posts by what they are about; a topic is rising when most of its posts are in the later half of the dates. A format or topic with fewer than {FEW} posts is shown but too few to compare.</p>
        </Card>
      </div>
    </div>
  );
}

function SpaceKpis({ s, name }) {
  const best = s.formats.find((f) => !f.few), topic = s.topics?.[0], big = [...s.profiles].filter((p) => p.followers !== null).sort((a, b) => b.followers - a.followers)[0];
  return (
    <div className="kpis span-12" role="group" aria-label={`LinkedIn numbers for ${name}`}>
      <Kpi label="Posts" value={nf.format(s.used)} sub={s.market === 'GLOBAL' ? `about ${name}` : `${s.inMarket} by authors in ${countryName(s.market)}${s.guessed ? ` (${s.guessed} an AI guess)` : ''}, ${s.unknown} with no country`} />
      <Kpi label="Left out" value={nf.format(s.elsewhere)} sub={s.market === 'GLOBAL' ? 'no market chosen' : 'by authors in other countries'} />
      <Kpi label="Most engaging topic" value={topic ? topic.name : '–'} text sub={topic ? `${plural(topic.posts, 'post')}, ${nf.format(topic.engagement)} engagement` : s.topicsNote ?? 'no topics'} />
      <Kpi label="Best format" value={best ? FORMAT_LABEL[best.format] ?? best.format : '–'} text sub={best ? `median ${nf.format(best.medianEngagement)} engagement a post` : `no format with ${FEW}+ posts`} />
      <Kpi label="Largest following" value={big ? compact(big.followers) : '–'} sub={big ? big.name : 'no follower counts'} />
    </div>
  );
}

function Topics({ s }) {
  const max = Math.max(1, ...(s.topics ?? []).map((t) => t.engagement));
  return (
    <Card span={12} title="What the space talks about" denom="Topics of the posts, most total engagement first." note={s.topics?.length ? null : s.topicsNote ?? 'No topics.'}>
      {s.topics?.length ? <div className="table-wrap"><table className="data space-table">
        <thead><tr><th>Topic</th><th className="num">Posts</th><th className="num hide-phone">Median a post</th><th className="barcol">Total engagement</th></tr></thead>
        <tbody>{s.topics.map((t) => (
          <tr key={t.name}>
            <td><b>{t.name}</b>{t.rising ? <> <span className="tag pos">Rising</span></> : null}{t.posts < FEW ? <> <span className="tag">Few posts</span></> : null}<div className="small muted">{t.what}{t.top ? <> · <a href={t.top} target="_blank" rel="noreferrer noopener">top post</a></> : null}</div></td>
            <td className="num">{nf.format(t.posts)}</td>
            <td className="num hide-phone">{nf.format(t.medianEngagement)}</td>
            <td className="barcol"><span className="hbar" style={{ width: `${Math.max(2, (t.engagement / max) * 100)}%` }} /><span className="small">{nf.format(t.engagement)}</span></td>
          </tr>
        ))}</tbody>
      </table></div> : null}
    </Card>
  );
}

function Formats({ s }) {
  const max = Math.max(1, ...s.formats.map((f) => f.medianEngagement));
  return (
    <Card span={12} title="Which formats work" denom="Median reactions and comments a post, per format." note={s.formats.length ? null : 'No formats.'}>
      {s.formats.length ? <div className="table-wrap"><table className="data space-table">
        <thead><tr><th>Format</th><th className="num">Posts</th><th className="barcol">Median engagement</th><th className="num hide-phone">Median comments</th></tr></thead>
        <tbody>{s.formats.map((f) => (
          <tr key={f.format} className={f.few ? 'muted' : undefined}>
            <td>{FORMAT_LABEL[f.format] ?? f.format}{f.few ? <> <span className="tag">Too few to compare</span></> : null}</td>
            <td className="num">{nf.format(f.posts)}</td>
            <td className="barcol"><span className="hbar" style={{ width: `${Math.max(2, (f.medianEngagement / max) * 100)}%` }} /><span className="small">{nf.format(f.medianEngagement)}</span></td>
            <td className="num hide-phone">{nf.format(f.medianComments)}</td>
          </tr>
        ))}</tbody>
      </table></div> : null}
    </Card>
  );
}

function TopPosts({ s, worldwide }) {
  const ps = worldwide ? s.worldwide : s.posts;
  return (
    <Card span={12} title={worldwide ? 'Leading posts worldwide' : 'Top posts'} denom={worldwide ? `By authors outside ${countryName(s.market)}, most engagement first: what leads the space elsewhere. For reference; not in the numbers above.` : 'Most engagement first.'} note={ps.length ? null : 'No posts.'}>
      <ol className="space-posts">{ps.map((p, i) => (
        <li key={p.url ?? i}>
          <div className="small"><b>{p.author ?? 'Unknown'}</b>{p.country ? ` · ${placeOf(p)}` : ''} · {FORMAT_LABEL[p.format] ?? p.format ?? 'post'}{p.date ? ` · ${fmtDate(p.date)}` : ''}</div>
          <p className="space-text">{p.text.replace(/\s+/g, ' ').slice(0, 180)}{p.text.length > 180 ? '…' : ''}</p>
          <div className="small muted">{plural(p.reactions, 'reaction')} · {plural(p.comments, 'comment')}{p.shares ? ` · ${plural(p.shares, 'repost')}` : ''}{p.url ? <> · <a href={p.url} target="_blank" rel="noreferrer noopener">Open on LinkedIn</a></> : null}</div>
        </li>
      ))}</ol>
    </Card>
  );
}

function Profiles({ s }) {
  return (
    <Card span={12} title="Top profiles in the space" denom="People and companies whose posts here drew the most engagement. Followers are LinkedIn’s figure at collection." note={s.profiles.length ? null : 'No profiles.'}>
      {s.profiles.length ? <div className="table-wrap"><table className="data space-table">
        <thead><tr><th>Profile</th><th className="hide-phone">Country</th><th className="num">Followers</th><th className="num hide-phone">Posts</th><th className="num">Engagement</th></tr></thead>
        <tbody>{s.profiles.map((p) => (
          <tr key={p.name}>
            <td>{p.url ? <a href={p.url} target="_blank" rel="noreferrer noopener">{p.name}</a> : p.name}{p.company ? <> <span className="tag">Company</span></> : null}{p.about ? <div className="small muted">{p.about.slice(0, 110)}</div> : null}</td>
            <td className="hide-phone">{placeOf(p) ?? '–'}</td>
            <td className="num">{p.followers === null ? '–' : compact(p.followers)}</td>
            <td className="num hide-phone">{nf.format(p.posts)}</td>
            <td className="num">{p.top ? <a href={p.top} target="_blank" rel="noreferrer noopener">{nf.format(p.engagement)}</a> : nf.format(p.engagement)}</td>
          </tr>
        ))}</tbody>
      </table></div> : null}
    </Card>
  );
}
